/**
 * "Let Claude do this" — the client-orchestrated tuning loop.
 *
 * Claude is NOT the optimiser. The allocation engine is deterministic, fast and
 * trusted; if Claude invented numbers the whole board paper would be worthless.
 * Claude's value is searching the parameter space and interpreting the result —
 * so THE SIMULATOR IS CLAUDE'S TOOL.
 *
 *   browser -> POST /api/research/studio-advisor { state, previous results }
 *           <- a validated JSON plan: "run these N candidates"
 *   browser runs them through the SAME engine the UI uses (instant, free)
 *           -> real numbers back to Claude, which refines or concludes
 *
 * Two reasons the loop lives in the browser rather than the API:
 *   1. It keeps each API call short, well inside the SWA ~45 second gateway
 *      limit, because no single call carries the search.
 *   2. Hallucination becomes structurally impossible. Every figure Claude ever
 *      quotes traces back to a candidate this engine actually scored.
 *
 * ⚠️ (1) IS ONLY TRUE IF EACH CALL IS ALSO SMALL. One call per round was not
 * enough: at effort high the model writes ~100 output tokens a second, so a
 * twelve-candidate round took 42-46s and the concluding round — which had to
 * produce the whole board narrative — took 47-49s and could never return. The
 * caps below and the two-phase conclusion are what keep the promise; see
 * api/src/functions/studioAdvisor.ts for the measurements.
 */
import type { Provenance, StudioMode } from './types'

/** Eight short rounds beat six long ones: same search, no call near the cliff. */
export const MAX_ROUNDS = 8
export const MAX_CANDIDATES_PER_ROUND = 6

/**
 * Abort a round that is going nowhere. The gateway gives up around 45s; this is
 * the backstop for a request that hangs instead of failing, so the user gets a
 * real message rather than a spinner that never stops.
 */
const REQUEST_TIMEOUT_MS = 70_000

/** The knobs Claude is allowed to move. Deliberately not "anything". */
export interface AdvisorSettings {
  tolerance: number
  recapturePct: number
  dwellMin: number
  farePct: number
  firstTrain: string
  lastTrain: string
  minRevPerDep: number
  objective: 'revenue' | 'profit'
  dieselLoco: number
  maxStay: boolean
  forceEarly: boolean
  /** Passengers allowed to be left unable to get home. Zero is the hard default. */
  maxUnreturned: number
  /** Mode B only. */
  mctMin: number
  aversionPct: number
  waitWeight: number
  nrPathsPerDay: number
  shuttleCoaches: number
  steamOverNR: boolean
}

export interface AdvisorCandidate {
  id: string
  /** One line on why this configuration is worth trying. */
  rationale: string
  mode: StudioMode
  settings: Partial<AdvisorSettings>
  /** Rebuild the timetable with Best fit under these settings. Almost always true. */
  bestFit: boolean
}

export interface AdvisorAssumption {
  name: string
  value: string
  provenance: Provenance
  note: string
}

export interface AdvisorRecommendation {
  candidateId: string
  headline: string
  /** Executive narrative. Lands in an editable panel, never straight in the PDF. */
  narrative: string
  /** Executive bullets: what actually changes vs today. Editable, like the narrative. */
  whatsChanged: string[]
  mechanism: string
  risks: string[]
  assumptions: AdvisorAssumption[]
  sensitivity: string
  /** What would change Claude's mind. */
  wouldChangeMind: string
}

/** What the concluding round returns: the decision, without the prose. */
export type AdvisorDecision = Omit<AdvisorRecommendation, 'narrative' | 'whatsChanged' | 'risks' | 'assumptions'>

/** What the follow-up 'narrative' call returns. */
export interface AdvisorNarrative {
  narrative: string
  whats_changed: string[]
  risks: string[]
  assumptions: AdvisorAssumption[]
}

export interface AdvisorPlan {
  done: boolean
  /** Progress note shown to the user between rounds. */
  message: string
  candidates: AdvisorCandidate[]
  recommendation?: AdvisorDecision
}

/** What the engine gives back for one candidate. All measured, none inferred. */
export interface CandidateResult {
  id: string
  mode: StudioMode
  revenue: number
  costs: number
  contribution: number
  carried: number
  lostSales: number
  unreturned: number
  lostInterchange: number
  connected: number
  services: number
  steamDiagrams: number
  dieselDiagrams: number
  carriages: number
  setsInTraffic: number
  nrPathsUsed: number
  nrPathsAvailable: number | null
  /** Paths over today's budget. Non-zero means Network Rail would have to agree. */
  nrPathsOver: number
  peakTransferWaiting: number
  meanTransferWaitMin: number
  fatalConflicts: number
  stockIssues: number
  fleetOverCap: string[]
  coalTonnes: number
  co2eTonnes: number
}

export interface AdvisorRound {
  round: number
  message: string
  candidates: AdvisorCandidate[]
  results: CandidateResult[]
}

export interface AdvisorRunLog {
  rounds: AdvisorRound[]
  recommendation: AdvisorRecommendation | null
  bestCandidateId: string | null
  error: string | null
}

interface AdvisorRequest {
  round: number
  /** 'plan' proposes or concludes; 'narrative' writes the concluded case up. */
  phase?: 'plan' | 'narrative'
  /** Sent with phase 'narrative' so the write-up cannot re-open the decision. */
  recommendation?: AdvisorDecision
  context: {
    season: string
    monthLabel: string
    dayClassLabel: string
    mode: StudioMode
    /** Peak-hour shape so Claude can reason about the problem, not just the score. */
    demandProfile: { time: string; north: number; south: number }[]
    baseline: CandidateResult
    settings: AdvisorSettings
    network: {
      stations: string[]
      nrSegment: string
      mctMin: number
      transferPlatforms: number
      nrPathsObservedToday: number
    }
    fleet: { maxSteam: number; maxDiesel: number; maxRailcar: number }
    sets: { code: string; coaches: number; stables: string }[]
    backtest: { mape: number; bias: number; slices: number } | null
    /**
     * Plain-English steer from the planner (Explore chat). INPUT ONLY — it is not
     * a settings knob and never becomes one; the model treats it as a constraint
     * on the search within the physics and honesty rules. Optional so old callers
     * and the harness are unaffected.
     */
    userSteer?: string
  }
  history: CompactRound[]
}

/** Round a number for the wire. Six decimal places of revenue help nobody. */
const r0 = (n: number): number => (Number.isFinite(n) ? Math.round(n) : 0)
const r1 = (n: number): number => (Number.isFinite(n) ? Math.round(n * 10) / 10 : 0)

/**
 * Trim the history before it goes back up.
 *
 * Sent verbatim, eight rounds of six candidates is ~37,000 input tokens — every
 * candidate repeating all eighteen settings even when it changed one, and the
 * engine's raw floats printed to six decimal places. That is slow to prefill and
 * it is more for the model to wade through, for numbers nobody would quote at
 * that precision. Three cuts, none of which lose information:
 *
 *   - settings as a DIFF from the run's own settings, so a candidate reads as
 *     the one thing it actually changed;
 *   - every figure rounded to what a person would say out loud;
 *   - no pretty-printing (the indentation alone was a third of the payload).
 *
 * Measured: ~37k input tokens down to ~9k.
 */
function compactRounds(rounds: AdvisorRound[], base: AdvisorSettings): CompactRound[] {
  const diff = (s: Partial<AdvisorSettings>): Partial<AdvisorSettings> => {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(s)) {
      if (v !== undefined && v !== (base as unknown as Record<string, unknown>)[k]) out[k] = v
    }
    return out as Partial<AdvisorSettings>
  }
  return rounds.map((rd) => ({
    round: rd.round,
    message: rd.message,
    candidates: rd.candidates.map((c) => ({
      id: c.id, rationale: c.rationale, mode: c.mode, bestFit: c.bestFit,
      changed: diff(c.settings),
    })),
    results: rd.results.map((x) => ({
      ...x,
      revenue: r0(x.revenue), costs: r0(x.costs), contribution: r0(x.contribution),
      carried: r0(x.carried), lostSales: r0(x.lostSales), unreturned: r0(x.unreturned),
      lostInterchange: r0(x.lostInterchange), connected: r0(x.connected),
      peakTransferWaiting: r0(x.peakTransferWaiting), meanTransferWaitMin: r0(x.meanTransferWaitMin),
      coalTonnes: r1(x.coalTonnes), co2eTonnes: r1(x.co2eTonnes),
    })),
  }))
}

/**
 * The evidence the WRITE-UP needs, which is far less than the search needed.
 *
 * The decision is already made by the time this call runs, so re-sending all
 * forty-odd scored candidates buys nothing and costs seconds of prefill. Send
 * the recommended candidate, the final round (where the sensitivity pass lives)
 * and the strongest handful — enough to keep every quotable figure grounded in
 * something the engine actually produced.
 */
function evidenceFor(rounds: AdvisorRound[], recommendedId: string, base: AdvisorSettings): CompactRound[] {
  const lastRound = rounds.length > 0 ? rounds[rounds.length - 1].round : 0
  const keep = new Set<string>([recommendedId])
  for (const rd of rounds) {
    if (rd.round === lastRound) for (const r of rd.results) keep.add(r.id)
  }
  const ranked = rounds
    .flatMap((rd) => rd.results)
    .filter((r) => Number.isFinite(r.contribution))
    .sort((a, b) => b.contribution - a.contribution)
  for (const r of ranked) {
    if (keep.size >= 12) break
    keep.add(r.id)
  }
  return compactRounds(
    rounds
      .map((rd) => ({
        ...rd,
        candidates: rd.candidates.filter((c) => keep.has(c.id)),
        results: rd.results.filter((r) => keep.has(r.id)),
      }))
      .filter((rd) => rd.results.length > 0),
    base
  )
}

/** A round as it goes back up the wire: settings reduced to what changed. */
interface CompactRound {
  round: number
  message: string
  candidates: {
    id: string
    rationale: string
    mode: StudioMode
    bestFit: boolean
    /** Only the settings that differ from the run's own — everything else is unchanged. */
    changed: Partial<AdvisorSettings>
  }[]
  results: CandidateResult[]
}

/**
 * One advisor call, with a hard timeout and a single retry.
 *
 * The gateway failure this guards against is transient by nature — a cold host
 * or a round that ran long — so one retry converts most of them into a normal
 * round. Without it a single hiccup used to discard every candidate scored so
 * far, which is a bad trade for a run that takes minutes.
 */
async function postAdvisor(body: AdvisorRequest, signal?: AbortSignal): Promise<unknown> {
  let lastErr: Error | null = null
  for (let attempt = 0; attempt < 2; attempt++) {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS)
    // A planner "Stop" aborts the in-flight round too, not just between rounds.
    const onAbort = () => ctrl.abort()
    if (signal) { if (signal.aborted) ctrl.abort(); else signal.addEventListener('abort', onAbort, { once: true }) }
    try {
      const res = await fetch('/api/research/studio-advisor', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      })
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: string; detail?: string }
        // 502/504 from the gateway carries no JSON body of ours — name it, so a
        // returning timeout is recognisable instead of looking like a model bug.
        const gateway = res.status === 500 || res.status === 502 || res.status === 504
        throw new Error(
          b.error || b.detail ||
          (gateway
            ? `the round did not come back in time (HTTP ${res.status})`
            : `advisor returned ${res.status}`)
        )
      }
      return await res.json()
    } catch (e) {
      lastErr = e instanceof Error && e.name === 'AbortError'
        ? new Error(`the round took longer than ${Math.round(REQUEST_TIMEOUT_MS / 1000)}s and was abandoned`)
        : (e as Error)
    } finally {
      clearTimeout(timer)
      if (signal) signal.removeEventListener('abort', onAbort)
    }
  }
  throw lastErr ?? new Error('the advisor call failed')
}

/**
 * Drive one full advisor run. `evaluate` is supplied by the Studio so the engine
 * wiring (network build, censoring, set planning, costing) stays in one place.
 */
export async function runAdvisor(
  context: AdvisorRequest['context'],
  evaluate: (c: AdvisorCandidate) => Promise<CandidateResult> | CandidateResult,
  onProgress?: (log: AdvisorRunLog) => void,
  signal?: AbortSignal
): Promise<AdvisorRunLog> {
  const log: AdvisorRunLog = { rounds: [], recommendation: null, bestCandidateId: null, error: null }

  // "Stop & keep best" salvage — used both between rounds and when the user
  // stops mid-round. Salvages what was scored AND WRITES THE BEST UP: stopping
  // after round 1 used to leave the narrative panel empty (the write-up only
  // ran at a natural conclusion); stopping means "stop searching", not "don't
  // tell me what you found".
  const salvage = async (round: number): Promise<AdvisorRunLog> => {
    log.bestCandidateId = bestOf(log)
    log.error = log.bestCandidateId
      ? `Search stopped — the best of the ${totalCandidates(log)} candidates already scored is shown; run it again to finish the search.`
      : 'Search stopped before any candidate was scored.'
    onProgress?.({ ...log, rounds: [...log.rounds] })
    if (log.bestCandidateId) {
      const bestRes = log.rounds.flatMap((r) => r.results).find((x) => x.id === log.bestCandidateId)
      const decision: AdvisorDecision = {
        candidateId: log.bestCandidateId,
        headline: `Best found before the search was stopped: ${log.bestCandidateId}${bestRes ? ` — contribution £${Math.round(bestRes.contribution).toLocaleString('en-GB')}, ${Math.round(bestRes.unreturned)} unreturned` : ''}. The search did not finish; treat as provisional.`,
        mechanism: '',
        sensitivity: 'Not measured — the search was stopped before a sensitivity round.',
        wouldChangeMind: 'Finishing the search: later rounds routinely beat the early best.',
      }
      log.recommendation = { ...decision, narrative: '', whatsChanged: [], risks: [], assumptions: [] }
      onProgress?.({ ...log, rounds: [...log.rounds] })
      try {
        // Deliberately NOT passing the user's abort signal: they asked to stop
        // the SEARCH; this one bounded call writes up what was found.
        const prose = (await postAdvisor({
          round, phase: 'narrative', recommendation: decision, context,
          history: evidenceFor(log.rounds, decision.candidateId, context.settings),
        })) as AdvisorNarrative
        log.recommendation = {
          ...decision,
          narrative: prose.narrative ?? '',
          whatsChanged: Array.isArray(prose.whats_changed) ? prose.whats_changed : [],
          risks: Array.isArray(prose.risks) ? prose.risks : [],
          assumptions: Array.isArray(prose.assumptions) ? prose.assumptions : [],
        }
      } catch {
        /* the salvage stands; the prose is a bonus after a manual stop */
      }
      onProgress?.({ ...log, rounds: [...log.rounds] })
    }
    return log
  }

  for (let round = 1; round <= MAX_ROUNDS; round++) {
    if (signal?.aborted) return salvage(round)
    const body: AdvisorRequest = {
      round, phase: 'plan', context, history: compactRounds(log.rounds, context.settings),
    }
    let plan: AdvisorPlan
    try {
      plan = (await postAdvisor(body, signal)) as AdvisorPlan
    } catch (e) {
      // The user stopping mid-round lands here (the fetch aborts) — that is a
      // salvage-and-write-up, not a failure.
      if (signal?.aborted) return salvage(round)
      // Keep what was scored. A failed round late in a run is a partial answer,
      // not a wasted one — surface the best candidate found rather than nothing.
      const salvaged = bestOf(log)
      log.bestCandidateId = salvaged
      log.error = salvaged
        ? `Round ${round} failed — ${String((e as Error)?.message ?? e)}. The best of the ${totalCandidates(log)} candidates already scored is shown; run it again to finish the search.`
        : String((e as Error)?.message ?? e)
      onProgress?.({ ...log, rounds: [...log.rounds] })
      return log
    }

    const candidates = (plan.candidates ?? []).slice(0, MAX_CANDIDATES_PER_ROUND)
    const results: CandidateResult[] = []
    for (const c of candidates) {
      try {
        results.push(await evaluate(c))
      } catch (e) {
        // A candidate the engine cannot score is reported, not silently dropped —
        // Claude needs to see that it proposed something impossible.
        results.push({
          id: c.id,
          mode: c.mode,
          revenue: 0, costs: 0, contribution: -Infinity, carried: 0, lostSales: 0,
          unreturned: 0, lostInterchange: 0, connected: 0, services: 0,
          steamDiagrams: 0, dieselDiagrams: 0, carriages: 0, setsInTraffic: 0,
          nrPathsUsed: 0, nrPathsAvailable: null, nrPathsOver: 0, peakTransferWaiting: 0,
          meanTransferWaitMin: 0, fatalConflicts: 0, stockIssues: 0,
          fleetOverCap: [`could not be evaluated: ${String((e as Error)?.message ?? e)}`],
          coalTonnes: 0, co2eTonnes: 0,
        })
      }
    }

    log.rounds.push({ round, message: plan.message ?? '', candidates, results })
    onProgress?.({ ...log, rounds: [...log.rounds] })

    if (plan.done) {
      const decision = plan.recommendation ?? null
      log.bestCandidateId = decision?.candidateId ?? bestOf(log)
      if (!decision) {
        onProgress?.({ ...log, rounds: [...log.rounds] })
        return log
      }
      // Show the decision straight away — the write-up is a second call and the
      // user should not stare at a spinner while it is fetched.
      log.recommendation = { ...decision, narrative: '', whatsChanged: [], risks: [], assumptions: [] }
      onProgress?.({ ...log, rounds: [...log.rounds] })

      try {
        const prose = (await postAdvisor({
          round, phase: 'narrative', recommendation: decision, context,
          history: evidenceFor(log.rounds, decision.candidateId, context.settings),
        }, signal)) as AdvisorNarrative
        log.recommendation = {
          ...decision,
          narrative: prose.narrative ?? '',
          whatsChanged: Array.isArray(prose.whats_changed) ? prose.whats_changed : [],
          risks: Array.isArray(prose.risks) ? prose.risks : [],
          assumptions: Array.isArray(prose.assumptions) ? prose.assumptions : [],
        }
      } catch (e) {
        // The decision stands; only the prose is missing, and Chris writes the
        // board paper anyway. Say so rather than failing a finished run.
        log.error = `The recommendation is complete, but the written case could not be drafted: ${String((e as Error)?.message ?? e)}`
      }
      onProgress?.({ ...log, rounds: [...log.rounds] })
      return log
    }
    if (candidates.length === 0) {
      // Nothing to try and not finished — stop rather than spin.
      log.error = 'Claude returned no candidates and did not conclude — stopping.'
      return log
    }
  }

  log.bestCandidateId = bestOf(log)
  log.error = `Reached the ${MAX_ROUNDS}-round limit without a conclusion. The best candidate found is shown.`
  return log
}

/** Highest contribution across every candidate that actually ran. */
export function bestOf(log: AdvisorRunLog): string | null {
  let best: CandidateResult | null = null
  for (const r of log.rounds) {
    for (const c of r.results) {
      if (!Number.isFinite(c.contribution)) continue
      if (c.fatalConflicts > 0 || c.fleetOverCap.length > 0) continue
      if (!best || c.contribution > best.contribution) best = c
    }
  }
  return best?.id ?? null
}

export function findCandidate(log: AdvisorRunLog, id: string | null): AdvisorCandidate | null {
  if (!id) return null
  for (const r of log.rounds) {
    const c = r.candidates.find((x) => x.id === id)
    if (c) return c
  }
  return null
}

export function findResult(log: AdvisorRunLog, id: string | null): CandidateResult | null {
  if (!id) return null
  for (const r of log.rounds) {
    const c = r.results.find((x) => x.id === id)
    if (c) return c
  }
  return null
}

export const totalCandidates = (log: AdvisorRunLog): number =>
  log.rounds.reduce((t, r) => t + r.results.length, 0)
